import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type * as Paths from "./paths";

interface OperationMethods {
  "getContacts"(
    parameters?: Parameters<Paths.GetContacts.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetContacts.Responses.$200>;

  "createContact"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateContact.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateContact.Responses.$200>;

  "getContact"(
    parameters?: Parameters<Paths.GetContact.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetContact.Responses.$200>;

  "updateContact"(
    parameters?: Parameters<Paths.UpdateContact.PathParameters> | null,
    data?: Paths.UpdateContact.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateContact.Responses.$200>;

  "deleteContact"(
    parameters?: Parameters<Paths.DeleteContact.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteContact.Responses.$204>;
}

export const getContactOperations = (client: AxiosInstance) => {
  const getContacts: OperationMethods["getContacts"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/contact";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createContact: OperationMethods["createContact"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/contact";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getContact: OperationMethods["getContact"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/contact/{contact}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateContact: OperationMethods["updateContact"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/contact/{contact}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteContact: OperationMethods["deleteContact"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/contact/{contact}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  return {
    getContacts,
    createContact,
    getContact,
    updateContact,
    deleteContact,
  };
};
